import React, { useEffect, useMemo, useState } from 'react';
import axios from '../config/api';
import { formatCurrency } from '../utils/format';
import { sortNewestFirst } from '../utils/newestFirst';
import {
  FaChartBar,
  FaChartLine,
  FaClipboardList,
  FaExclamationTriangle,
  FaEye,
  FaProjectDiagram,
  FaShoppingCart,
  FaStar,
  FaStore,
  FaUtensils,
  FaWifi,
} from 'react-icons/fa';
import { useFunnelCounts } from '../hooks/useFunnelCounts';
import { useOrdersReport } from '../hooks/useOrdersReport';
import { isCompletedOrderStatus, ordersInRange } from '../utils/reportRange';
import {
  computeOnlineRateMetrics,
  type StoreHoursConfig,
} from '../utils/storeHours';
import {
  STUCK_MIN,
  MISSED_READY_MIN,
  isCancelledIssue,
  isStuckIssue,
  isLongReadyIssue,
  matchesIssueHeatmapFilter,
  issueHeatmapHour,
  type IssueHeatmapFilter,
} from '../utils/orderIssueMetrics';

export type PerformanceTimeRange = 'today' | 'week' | '30d' | '90d' | 'custom';

/** Matches the six Performance sidebar submenu items. */
export type PerformanceSectionId =
  | 'sales-volume-ticket'
  | 'chart-leaderboards'
  | 'online-rate'
  | 'order-issues'
  | 'customer-feedback'
  | 'conversion-funnel';

export const PERFORMANCE_NAV_ITEMS: { id: PerformanceSectionId; label: string }[] = [
  {
    id: 'sales-volume-ticket',
    label: 'Sales, order volume & ticket size',
  },
  {
    id: 'chart-leaderboards',
    label: 'Sales over time & leaderboards',
  },
  { id: 'online-rate', label: 'Online rate' },
  { id: 'order-issues', label: 'Order issues' },
  { id: 'customer-feedback', label: 'Customer feedback' },
  { id: 'conversion-funnel', label: 'Conversion funnel' },
];

interface CafeLite {
  id: string;
  name: string;
}

interface ReviewRow {
  id: string;
  rating: number;
  comment: string | null;
  created_at: string;
  user_name?: string;
  cafe_id?: string;
  staff_reply?: string | null;
  staff_reply_at?: string | null;
}

const STAFF_REPLY_MAX = 2000;

function startOfDay(d: Date): Date {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}

function addDays(d: Date, n: number): Date {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
}

function getRangeBounds(
  range: PerformanceTimeRange,
  customStart: string,
  customEnd: string
): { start: Date; end: Date } {
  const end = new Date();
  const today = startOfDay(end);

  switch (range) {
    case 'today': {
      const dayEnd = new Date(today);
      dayEnd.setHours(23, 59, 59, 999);
      return { start: today, end: dayEnd };
    }
    case 'week':
      return { start: addDays(today, -6), end };
    case '30d':
      return { start: addDays(today, -29), end };
    case '90d':
      return { start: addDays(today, -89), end };
    case 'custom': {
      if (!customStart || !customEnd) {
        return { start: addDays(today, -6), end };
      }
      const start = new Date(`${customStart}T00:00:00`);
      const customEndDate = new Date(`${customEnd}T23:59:59.999`);
      if (Number.isNaN(start.getTime()) || Number.isNaN(customEndDate.getTime())) {
        return { start: addDays(today, -6), end };
      }
      if (start.getTime() > customEndDate.getTime()) {
        return { start: customEndDate, end: start };
      }
      return { start, end: customEndDate };
    }
    default:
      return { start: addDays(today, -6), end };
  }
}

interface PerformanceHealthPanelProps {
  cafes: CafeLite[];
  selectedCafe: string;
  storeHours?: StoreHoursConfig | null;
  onNavigateToStoreHours?: () => void;
  /** When omitted (e.g. older call sites), defaults to sales KPIs. */
  activeSection?: PerformanceSectionId;
}

export default function PerformanceHealthPanel({
  cafes,
  selectedCafe,
  storeHours = null,
  onNavigateToStoreHours,
  activeSection = 'sales-volume-ticket',
}: PerformanceHealthPanelProps) {
  const [timeRange, setTimeRange] = useState<PerformanceTimeRange>('today');
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const [reviews, setReviews] = useState<ReviewRow[]>([]);
  const [reviewsLoading, setReviewsLoading] = useState(false);
  const [issueHeatmapFilter, setIssueHeatmapFilter] = useState<IssueHeatmapFilter>('all');
  const [replyDrafts, setReplyDrafts] = useState<Record<string, string>>({});
  const [replySavingId, setReplySavingId] = useState<string | null>(null);
  const [replyError, setReplyError] = useState<string | null>(null);

  const { start, end } = useMemo(
    () => getRangeBounds(timeRange, customStart, customEnd),
    [timeRange, customStart, customEnd]
  );
  const ordersReport = useOrdersReport(selectedCafe, start, end);

  const scopedOrders = useMemo(() => {
    const periodOrders = ordersInRange(ordersReport.orders, start, end);
    if (selectedCafe) {
      return periodOrders.filter((o) => o.cafe_id === selectedCafe);
    }
    return periodOrders;
  }, [ordersReport.orders, selectedCafe, start, end]);

  useEffect(() => {
    if (timeRange !== 'custom') return;
    const now = new Date();
    const todayStr = now.toISOString().slice(0, 10);
    const sevenDaysAgo = addDays(startOfDay(now), -6).toISOString().slice(0, 10);
    if (!customStart) setCustomStart(sevenDaysAgo);
    if (!customEnd) setCustomEnd(todayStr);
  }, [timeRange, customStart, customEnd]);

  const rangeOrders = scopedOrders;

  const completedInRange = useMemo(
    () => rangeOrders.filter((o) => isCompletedOrderStatus(o.status)),
    [rangeOrders]
  );

  const salesTotal = useMemo(
    () => completedInRange.reduce((s, o) => s + o.total_amount, 0),
    [completedInRange]
  );

  const orderVolume = completedInRange.length;

  const funnelCounts = useFunnelCounts(
    selectedCafe,
    start,
    end,
    activeSection === 'conversion-funnel'
  );

  const ticketSize = orderVolume > 0 ? salesTotal / orderVolume : 0;

  const onlineRate = useMemo(
    () =>
      computeOnlineRateMetrics(
        start,
        end,
        storeHours,
        completedInRange.map((o) => new Date(o.created_at))
      ),
    [completedInRange, start, end, storeHours]
  );

  const acceptingOrdersNow = storeHours?.accepting_orders !== false;

  const chartSeries = useMemo(() => {
    const completed = completedInRange;

    if (timeRange === 'custom') {
      const labels: string[] = [];
      const values: number[] = [];
      const cursor = startOfDay(start);
      const endDay = startOfDay(end);
      while (cursor <= endDay) {
        const dayStart = new Date(cursor);
        const dayEnd = new Date(cursor);
        dayEnd.setHours(23, 59, 59, 999);
        labels.push(
          dayStart.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
        );
        let sum = 0;
        completed.forEach((o) => {
          const t = new Date(o.created_at);
          if (t >= dayStart && t <= dayEnd) sum += o.total_amount;
        });
        values.push(sum);
        cursor.setDate(cursor.getDate() + 1);
      }
      return { labels, values };
    }

    const dayCount = timeRange === 'today' ? 1 : timeRange === 'week' ? 7 : timeRange === '30d' ? 30 : 90;

    if (timeRange === '90d') {
      const labels: string[] = [];
      const values: number[] = [];
      const endDay = startOfDay(end);
      for (let w = 12; w >= 0; w--) {
        const weekEndDay = addDays(endDay, -w * 7);
        const weekStart = addDays(weekEndDay, -6);
        const weekEndBound = new Date(weekEndDay);
        weekEndBound.setHours(23, 59, 59, 999);
        labels.push(
          weekStart.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
        );
        let sum = 0;
        completed.forEach((o) => {
          const t = new Date(o.created_at);
          if (t >= weekStart && t <= weekEndBound) sum += o.total_amount;
        });
        values.push(sum);
      }
      return { labels, values };
    }

    const labels: string[] = [];
    const values: number[] = [];
    for (let i = dayCount - 1; i >= 0; i--) {
      const day = addDays(startOfDay(end), -i);
      const dayStart = new Date(day);
      const dayEnd = new Date(day);
      dayEnd.setHours(23, 59, 59, 999);
      labels.push(
        timeRange === 'week'
          ? day.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' })
          : day.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
      );
      let sum = 0;
      completed.forEach((o) => {
        const t = new Date(o.created_at);
        if (t >= dayStart && t <= dayEnd) sum += o.total_amount;
      });
      values.push(sum);
    }
    return { labels, values };
  }, [completedInRange, timeRange, start, end]);

  const maxChart = useMemo(() => Math.max(...chartSeries.values, 1), [chartSeries.values]);

  const storeLeaderboard = useMemo(() => {
    const map: Record<string, { name: string; sales: number; count: number }> = {};
    completedInRange.forEach((o) => {
      if (!map[o.cafe_id]) {
        map[o.cafe_id] = { name: o.cafe_name, sales: 0, count: 0 };
      }
      map[o.cafe_id].sales += o.total_amount;
      map[o.cafe_id].count += 1;
    });
    return Object.entries(map)
      .map(([id, v]) => ({ id, ...v }))
      .sort((a, b) => b.sales - a.sales)
      .slice(0, 8);
  }, [completedInRange]);

  const itemLeaderboard = useMemo(() => {
    const map: Record<string, { name: string; units: number }> = {};
    completedInRange.forEach((o) => {
      o.items.forEach((it) => {
        const id = it.menu_item_id;
        const name = it.name || id.slice(0, 8);
        if (!map[id]) map[id] = { name, units: 0 };
        map[id].units += it.quantity;
      });
    });
    return Object.entries(map)
      .map(([id, v]) => ({ id, ...v }))
      .sort((a, b) => b.units - a.units)
      .slice(0, 8);
  }, [completedInRange]);

  const issueHeatmap = useMemo(() => {
    const counts = Array(24).fill(0);
    rangeOrders.forEach((o) => {
      if (!matchesIssueHeatmapFilter(o, issueHeatmapFilter)) return;
      const h = issueHeatmapHour(o, issueHeatmapFilter);
      counts[h] += 1;
    });
    const max = Math.max(...counts, 1);
    return { counts, max };
  }, [rangeOrders, issueHeatmapFilter]);

  const issueTrends = useMemo(() => {
    const cancelled = rangeOrders.filter(isCancelledIssue).length;
    const missed = rangeOrders.filter(isLongReadyIssue).length;
    const stuck = rangeOrders.filter(isStuckIssue).length;
    return { cancelled, missed, stuck, totalIssues: cancelled + missed + stuck };
  }, [rangeOrders]);

  const issueFilterLabels: Record<IssueHeatmapFilter, string> = {
    all: 'All issue types',
    cancelled: 'Cancelled only',
    stuck: `Stuck > ${STUCK_MIN}m only`,
    long_ready: `Long ready (>${MISSED_READY_MIN}m) only`,
  };

  const issueFilterDescriptions: Record<IssueHeatmapFilter, string> = {
    all: 'Combined view of cancelled, stuck, and long-ready orders in the heatmap.',
    cancelled: 'Orders with status cancelled during the selected period.',
    stuck: `Still-open orders (pending or preparing) that have not moved to the next status for more than ${STUCK_MIN} minutes—e.g. not acknowledged, drink not started, or the ticket was not updated on the bar.`,
    long_ready: `Orders marked ready for more than ${MISSED_READY_MIN} minutes without pickup, or that sat in ready too long before completion (possible missed pickup).`,
  };

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setReviewsLoading(true);
      try {
        if (selectedCafe) {
          const { data } = await axios.get<ReviewRow[]>(`/api/reviews/cafe/${selectedCafe}`);
          if (!cancelled) setReviews(sortNewestFirst(data || [], (review) => review.created_at));
        } else if (cafes.length > 0) {
          const results = await Promise.all(
            cafes.map((c) =>
              axios.get<ReviewRow[]>(`/api/reviews/cafe/${c.id}`).then((r) => r.data || [])
            )
          );
          if (!cancelled) {
            setReviews(sortNewestFirst(results.flat(), (review) => review.created_at));
          }
        } else {
          if (!cancelled) setReviews([]);
        }
      } catch {
        if (!cancelled) setReviews([]);
      } finally {
        if (!cancelled) setReviewsLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [selectedCafe, cafes]);

  const avgRating =
    reviews.length > 0
      ? reviews.reduce((s, r) => s + r.rating, 0) / reviews.length
      : null;

  const rangeLabels: Record<PerformanceTimeRange, string> = {
    today: 'Today',
    week: 'Past week',
    '30d': 'Past 30 days',
    '90d': 'Past 90 days',
    custom: 'Custom range',
  };

  const PERFORMANCE_TIME_RANGES: PerformanceTimeRange[] = ['today', 'week', '30d', '90d', 'custom'];

  const selectedRangeLabel = useMemo(() => {
    if (timeRange === 'custom' && customStart && customEnd) {
      return `${customStart} to ${customEnd}`;
    }
    return rangeLabels[timeRange];
  }, [timeRange, customStart, customEnd]);

  const timeRangeToolbar = (
    <div className="bg-white rounded-lg shadow-md p-4 border border-gray-100 mb-6">
      <div className="flex flex-col gap-3">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <span className="text-sm font-semibold text-gray-700">Reporting period</span>
          <div className="flex flex-wrap gap-2">
            {PERFORMANCE_TIME_RANGES.map((key) => (
              <button
                key={key}
                type="button"
                onClick={() => setTimeRange(key)}
                className={`px-3 py-1.5 rounded-full text-xs font-semibold transition ${
                  timeRange === key
                    ? 'bg-amber-700 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {rangeLabels[key]}
              </button>
            ))}
          </div>
        </div>
        {timeRange === 'custom' && (
          <div className="flex flex-col sm:flex-row sm:items-end gap-3">
            <label className="text-xs font-semibold text-gray-600">
              Start date
              <input
                type="date"
                value={customStart}
                max={customEnd || undefined}
                onChange={(e) => setCustomStart(e.target.value)}
                className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500"
              />
            </label>
            <label className="text-xs font-semibold text-gray-600">
              End date
              <input
                type="date"
                value={customEnd}
                min={customStart || undefined}
                onChange={(e) => setCustomEnd(e.target.value)}
                className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500"
              />
            </label>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div className="space-y-8">
      {activeSection !== 'customer-feedback' && timeRangeToolbar}
      {activeSection !== 'customer-feedback' && ordersReport.loading && (
        <p className="text-sm text-gray-500">Loading completed orders for this period…</p>
      )}
      {activeSection !== 'customer-feedback' && ordersReport.error && (
        <p className="text-sm text-red-700">{ordersReport.error}</p>
      )}

      {activeSection === 'sales-volume-ticket' && (
        <section className="bg-white rounded-lg shadow-md p-6 border border-gray-100">
          <h2 className="text-xl font-bold text-amber-900 flex items-center gap-2 mb-2">
            <FaChartLine className="text-amber-700" />
            Sales, order volume & ticket size
          </h2>
          <p className="text-sm text-gray-600 mb-6 max-w-3xl">
            Monitor these metrics for today, or switch to the past week, 30 days, 90 days, or a
            custom range to track revenue trends and customer spending habits.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="bg-amber-50 rounded-lg p-4 border border-amber-100">
              <div className="text-xs font-semibold text-amber-800 uppercase tracking-wide">
                Sales (completed)
              </div>
              <div className="text-2xl font-bold text-gray-900 mt-1">
                {ordersReport.loading ? '…' : formatCurrency(salesTotal)}
              </div>
              <p className="text-xs text-gray-600 mt-2">{selectedRangeLabel}</p>
            </div>
            <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
              <div className="text-xs font-semibold text-slate-700 uppercase tracking-wide flex items-center gap-1">
                <FaClipboardList className="inline" /> Order volume
              </div>
              <div className="text-2xl font-bold text-gray-900 mt-1">
                {ordersReport.loading ? '…' : orderVolume}
              </div>
              <p className="text-xs text-gray-600 mt-2">Completed orders in range</p>
            </div>
            <div className="bg-emerald-50 rounded-lg p-4 border border-emerald-100">
              <div className="text-xs font-semibold text-emerald-800 uppercase tracking-wide">
                Avg ticket size
              </div>
              <div className="text-2xl font-bold text-gray-900 mt-1">
                {ordersReport.loading
                  ? '…'
                  : orderVolume > 0
                    ? formatCurrency(ticketSize)
                    : '—'}
              </div>
              <p className="text-xs text-gray-600 mt-2">Revenue ÷ completed orders</p>
            </div>
          </div>
        </section>
      )}

      {activeSection === 'chart-leaderboards' && (
        <section className="bg-white rounded-lg shadow-md p-6 border border-gray-100">
          <h2 className="text-xl font-bold text-amber-900 flex items-center gap-2 mb-2">
            <FaChartBar className="text-amber-700" />
            Sales over time & leaderboards
          </h2>
          <p className="text-sm text-gray-600 mb-6 max-w-3xl">
            Use the chart and leaderboards to identify top-performing stores or menu items for
            the selected period.
          </p>

          <div className="mb-8">
            <h3 className="text-sm font-bold text-gray-800 mb-3 flex items-center gap-2">
              <FaChartBar className="text-amber-600" />
              Sales over time (completed orders)
            </h3>
            <div className="flex items-end gap-1 h-40 border-b border-gray-200 pb-1 px-1 overflow-x-auto">
              {chartSeries.values.map((v, i) => (
                <div
                  key={i}
                  className="flex flex-col items-center flex-1 min-w-[24px] max-w-[48px]"
                  title={`${chartSeries.labels[i]}: ${formatCurrency(v)}`}
                >
                  <div
                    className="w-full bg-amber-500 rounded-t transition-all hover:bg-amber-600"
                    style={{ height: `${(v / maxChart) * 100}%`, minHeight: v > 0 ? 4 : 0 }}
                  />
                </div>
              ))}
            </div>
            <div className="flex justify-between gap-1 mt-2 text-[10px] text-gray-500 overflow-x-auto">
              {chartSeries.labels.map((lab, i) => (
                <span key={i} className="flex-1 min-w-[24px] text-center truncate">
                  {lab}
                </span>
              ))}
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <h3 className="text-sm font-bold text-gray-800 mb-3 flex items-center gap-2">
                <FaStore className="text-amber-600" />
                Store leaderboard
              </h3>
              {ordersReport.loading ? (
                <p className="text-sm text-gray-500">Loading…</p>
              ) : storeLeaderboard.length === 0 ? (
                <p className="text-sm text-gray-500">No completed orders in this range.</p>
              ) : (
                <ul className="space-y-2">
                  {storeLeaderboard.map((row, idx) => (
                    <li
                      key={row.id}
                      className="flex justify-between items-center text-sm border rounded-lg px-3 py-2 bg-gray-50"
                    >
                      <span className="text-gray-700">
                        <span className="font-semibold text-amber-800">{idx + 1}.</span> {row.name}
                      </span>
                      <span className="font-semibold text-gray-900">
                        {formatCurrency(row.sales)}
                        <span className="text-gray-500 font-normal text-xs ml-1">
                          ({row.count} orders)
                        </span>
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div>
              <h3 className="text-sm font-bold text-gray-800 mb-3 flex items-center gap-2">
                <FaUtensils className="text-amber-600" />
                Top menu items (units sold)
              </h3>
              {ordersReport.loading ? (
                <p className="text-sm text-gray-500">Loading…</p>
              ) : itemLeaderboard.length === 0 ? (
                <p className="text-sm text-gray-500">No line items in completed orders.</p>
              ) : (
                <ul className="space-y-2">
                  {itemLeaderboard.map((row, idx) => (
                    <li
                      key={row.id}
                      className="flex justify-between items-center text-sm border rounded-lg px-3 py-2 bg-gray-50"
                    >
                      <span className="text-gray-700">
                        <span className="font-semibold text-amber-800">{idx + 1}.</span> {row.name}
                      </span>
                      <span className="font-semibold text-gray-900">{row.units} units</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </section>
      )}

      {activeSection === 'online-rate' && (
      <section className="bg-white rounded-lg shadow-md p-6 border border-gray-100">
        <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2 mb-2">
          <FaWifi className="text-emerald-600" />
          Online rate
        </h2>
        <p className="text-sm text-gray-600 mb-4 max-w-3xl">
          Compares your <span className="font-semibold">scheduled open days</span> (from Settings)
          to days with at least one completed order in{' '}
          <span className="font-semibold">{selectedRangeLabel}</span>.
          {!selectedCafe && (
            <>
              {' '}
              Pick a café under Location for café-specific hours.
            </>
          )}
        </p>
        {!acceptingOrdersNow && (
          <p className="mb-4 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-950">
            This café is marked <span className="font-semibold">not accepting orders</span> in
            Settings. Turn availability back on when you are open.
          </p>
        )}
        <div className="grid md:grid-cols-2 gap-4">
          <div className="rounded-lg border border-emerald-200 p-4 bg-emerald-50">
            <div className="text-xs font-semibold text-emerald-800 uppercase">Online rate</div>
            <div className="text-3xl font-bold text-emerald-900 mt-2">
              {onlineRate.hasSchedule ? `${onlineRate.onlineRatePct}%` : '—'}
            </div>
            <p className="text-xs text-gray-700 mt-2">
              {onlineRate.hasSchedule
                ? `${onlineRate.activeOpenDays} of ${onlineRate.scheduledOpenDays} scheduled open days had at least one completed order.`
                : 'Set store hours in Settings (at least one open day per week).'}
            </p>
            {onNavigateToStoreHours && (
              <button
                type="button"
                onClick={onNavigateToStoreHours}
                className="mt-3 text-xs font-semibold text-amber-800 underline hover:text-amber-950"
              >
                Edit store hours in Settings →
              </button>
            )}
          </div>
          <div className="rounded-lg border border-gray-200 p-4 bg-gray-50">
            <div className="text-xs font-semibold text-gray-600 uppercase">Live availability</div>
            <div
              className={`text-2xl font-bold mt-2 ${
                acceptingOrdersNow ? 'text-emerald-800' : 'text-amber-700'
              }`}
            >
              {acceptingOrdersNow ? 'Accepting orders' : 'Paused'}
            </div>
            <p className="text-xs text-gray-600 mt-2">
              Staff toggle in Settings — use when closed unexpectedly outside your weekly hours.
            </p>
          </div>
        </div>
      </section>
      )}

      {activeSection === 'order-issues' && (
      <section className="bg-white rounded-lg shadow-md p-6 border border-gray-100">
        <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2 mb-2">
          <FaExclamationTriangle className="text-orange-500" />
          Order issues
        </h2>
        <p className="text-sm text-gray-600 mb-4 max-w-3xl">
          Review trends in inaccurate, missed, and cancelled orders. The heatmap by hour
          highlights peak problem windows—use it for staffing and process adjustments.
          Counts below are for the <span className="font-semibold">selected reporting period</span>{' '}
          and <span className="font-semibold">café filter</span> (same orders loaded in this
          dashboard). Tap a category to filter the heatmap; numbers stay as totals for context.
        </p>
        <ul className="text-xs text-gray-500 mb-4 max-w-3xl space-y-1.5 list-none">
          <li>
            <span className="font-semibold text-gray-700">Cancelled</span> — orders with status
            cancelled.
          </li>
          <li>
            <span className="font-semibold text-gray-700">Stuck</span> — still-open orders that have
            not moved to the next status for more than {STUCK_MIN} minutes (since the last status
            change). Typical causes: order not acknowledged, drink not started, or barista forgot
            to advance the ticket.
          </li>
          <li>
            <span className="font-semibold text-gray-700">Long ready</span> — in &ldquo;ready&rdquo;
            for more than {MISSED_READY_MIN} minutes, or took that long between ready and completed
            (possible missed pickup).
          </li>
        </ul>
        <div className="flex flex-wrap gap-2 mb-2 text-sm">
          <button
            type="button"
            onClick={() => setIssueHeatmapFilter('all')}
            className={`px-3 py-1.5 rounded-full font-medium border-2 transition ${
              issueHeatmapFilter === 'all'
                ? 'border-gray-800 bg-gray-800 text-white'
                : 'border-gray-200 bg-gray-50 text-gray-800 hover:bg-gray-100'
            }`}
          >
            All types · {issueTrends.totalIssues}
          </button>
          <button
            type="button"
            onClick={() => setIssueHeatmapFilter('cancelled')}
            className={`px-3 py-1.5 rounded-full font-medium border-2 transition ${
              issueHeatmapFilter === 'cancelled'
                ? 'border-red-800 bg-red-800 text-white'
                : 'border-red-200 bg-red-100 text-red-900 hover:bg-red-200/80'
            }`}
          >
            Cancelled: {issueTrends.cancelled}
          </button>
          <button
            type="button"
            onClick={() => setIssueHeatmapFilter('stuck')}
            className={`px-3 py-1.5 rounded-full font-medium border-2 transition ${
              issueHeatmapFilter === 'stuck'
                ? 'border-amber-800 bg-amber-800 text-white'
                : 'border-amber-200 bg-amber-100 text-amber-900 hover:bg-amber-200/80'
            }`}
          >
            Stuck &gt; {STUCK_MIN}m: {issueTrends.stuck}
          </button>
          <button
            type="button"
            onClick={() => setIssueHeatmapFilter('long_ready')}
            className={`px-3 py-1.5 rounded-full font-medium border-2 transition ${
              issueHeatmapFilter === 'long_ready'
                ? 'border-orange-800 bg-orange-800 text-white'
                : 'border-orange-200 bg-orange-100 text-orange-900 hover:bg-orange-200/80'
            }`}
          >
            Long ready: {issueTrends.missed}
          </button>
        </div>
        <p className="text-xs text-gray-600 mb-3 max-w-3xl rounded-lg bg-gray-50 border border-gray-100 px-3 py-2">
          {issueFilterDescriptions[issueHeatmapFilter]}
        </p>
        <div className="text-xs font-semibold text-gray-600 mb-2">
          Issues by hour (local time) — {issueFilterLabels[issueHeatmapFilter]}
        </div>
        <div
          className="grid gap-1 w-full"
          style={{ gridTemplateColumns: 'repeat(24, minmax(0, 1fr))' }}
        >
          {issueHeatmap.counts.map((c, h) => {
            const intensity = c / issueHeatmap.max;
            const bg =
              c === 0
                ? 'bg-gray-100'
                : intensity < 0.34
                ? 'bg-orange-200'
                : intensity < 0.67
                ? 'bg-orange-400'
                : 'bg-orange-600';
            return (
              <div
                key={h}
                title={`${h}:00 — ${c} · ${issueFilterLabels[issueHeatmapFilter]}`}
                className={`aspect-square rounded ${bg} min-w-[18px] flex items-center justify-center text-[9px] font-medium ${
                  c > 0 ? 'text-white' : 'text-gray-400'
                }`}
              >
                {h % 4 === 0 ? h : ''}
              </div>
            );
          })}
        </div>
      </section>
      )}

      {activeSection === 'customer-feedback' && (
      <section className="bg-white rounded-lg shadow-md p-6 border border-gray-100">
        <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2 mb-2">
          <FaStar className="text-amber-500" />
          Customer feedback
        </h2>
        <p className="text-sm text-gray-600 mb-4 max-w-3xl">
          Average ratings and recent comments for your restaurant, newest first. When a
          customer leaves a comment, you can post a public-facing reply from here (stored on
          the review).
        </p>
        {reviewsLoading ? (
          <p className="text-sm text-gray-500">Loading reviews…</p>
        ) : (
          <>
            {replyError && (
              <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2 mb-3">
                {replyError}
              </p>
            )}
            <div className="flex items-baseline gap-3 mb-4">
              <span className="text-3xl font-bold text-gray-900">
                {avgRating != null ? avgRating.toFixed(2) : '—'}
              </span>
              <span className="text-amber-500">★★★★★</span>
              <span className="text-sm text-gray-500">
                ({reviews.length} review{reviews.length !== 1 ? 's' : ''}
                {selectedCafe ? '' : ' · all locations'})
              </span>
            </div>
            {reviews.length === 0 ? (
              <p className="text-sm text-gray-500">No reviews yet for this selection.</p>
            ) : (
              <ul className="space-y-4 max-h-[32rem] overflow-y-auto pr-1">
                {reviews.slice(0, 12).map((r) => {
                  const draft = replyDrafts[r.id] ?? r.staff_reply ?? '';
                  return (
                    <li
                      key={r.id}
                      className="text-sm border border-gray-100 rounded-lg p-3 bg-gray-50/80"
                    >
                      <div className="flex justify-between gap-2">
                        <span className="font-semibold text-amber-800">
                          {r.rating}/5 · {r.user_name || 'Customer'}
                        </span>
                        <span className="text-xs text-gray-400 whitespace-nowrap">
                          {new Date(r.created_at).toLocaleString()}
                        </span>
                      </div>
                      {r.comment ? (
                        <p className="text-gray-700 mt-2 whitespace-pre-wrap">{r.comment}</p>
                      ) : (
                        <p className="text-gray-500 mt-2 italic text-xs">No written comment</p>
                      )}
                      {r.staff_reply && (
                        <div className="mt-3 rounded-md border border-amber-200 bg-amber-50/90 px-3 py-2">
                          <div className="text-xs font-semibold text-amber-900 uppercase tracking-wide">
                            Your reply
                            {r.staff_reply_at && (
                              <span className="font-normal text-amber-800 normal-case ml-2">
                                · {new Date(r.staff_reply_at).toLocaleString()}
                              </span>
                            )}
                          </div>
                          <p className="text-gray-800 mt-1 whitespace-pre-wrap">{r.staff_reply}</p>
                        </div>
                      )}
                      <label className="block mt-3">
                        <span className="text-xs font-semibold text-gray-600">
                          {r.staff_reply ? 'Update reply' : 'Reply to customer'}
                        </span>
                        <textarea
                          value={draft}
                          maxLength={STAFF_REPLY_MAX}
                          onChange={(e) =>
                            setReplyDrafts((prev) => ({ ...prev, [r.id]: e.target.value }))
                          }
                          rows={3}
                          placeholder="Thank them, clarify an issue, or share next steps…"
                          className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500"
                        />
                        <span className="text-[11px] text-gray-500">
                          {draft.length}/{STAFF_REPLY_MAX}
                        </span>
                      </label>
                      <button
                        type="button"
                        disabled={replySavingId === r.id || !draft.trim()}
                        onClick={async () => {
                          const text = draft.trim();
                          if (!text) return;
                          setReplyError(null);
                          setReplySavingId(r.id);
                          try {
                            const { data } = await axios.put<ReviewRow>(
                              `/api/reviews/${r.id}/staff-reply`,
                              { reply: text }
                            );
                            setReviews((prev) =>
                              prev.map((row) => (row.id === r.id ? { ...row, ...data } : row))
                            );
                            setReplyDrafts((prev) => ({ ...prev, [r.id]: data.staff_reply || '' }));
                          } catch (err: unknown) {
                            const ax = err as { response?: { data?: { error?: string } } };
                            setReplyError(
                              ax.response?.data?.error || 'Could not save reply. Try again.'
                            );
                          } finally {
                            setReplySavingId(null);
                          }
                        }}
                        className="mt-2 inline-flex items-center px-3 py-1.5 rounded-lg bg-amber-700 text-white text-xs font-semibold hover:bg-amber-800 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {replySavingId === r.id ? 'Saving…' : r.staff_reply ? 'Update reply' : 'Post reply'}
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
            <p className="text-xs text-gray-500 mt-4">
              <span className="font-semibold">Menu item feedback:</span> tie comments to
              best-selling SKUs from the leaderboard above to prioritize recipe or quality
              fixes.
            </p>
          </>
        )}
      </section>
      )}

      {activeSection === 'conversion-funnel' && (
      <section className="bg-white rounded-lg shadow-md p-6 border border-gray-100">
        <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2 mb-2">
          <FaProjectDiagram className="text-amber-600" />
          Conversion funnel
        </h2>
        <p className="text-sm text-gray-600 mb-6 max-w-3xl">
          See where shoppers drop off between browsing and purchase. Counts are unique browser
          sessions per step for the <span className="font-semibold">{selectedRangeLabel}</span>{' '}
          and <span className="font-semibold">café filter</span> (menu views, cart adds, and checkout
          taps are recorded from the customer app).
        </p>
        <div className="space-y-3">
          <FunnelStep
            icon={<FaEye className="text-gray-500" />}
            label="Store / menu views"
            value={
              funnelCounts.loading ? '…' : String(funnelCounts.menu_views)
            }
            sub="Distinct sessions that opened this café menu."
            dim={false}
          />
          <FunnelStep
            icon={<FaShoppingCart className="text-gray-500" />}
            label="Add to cart"
            value={
              funnelCounts.loading ? '…' : String(funnelCounts.cart_adds)
            }
            sub="Sessions that added at least one item."
            dim={false}
          />
          <FunnelStep
            icon={<FaClipboardList className="text-amber-600" />}
            label="Checkout started"
            value={
              funnelCounts.loading ? '…' : String(funnelCounts.checkout_started)
            }
            sub="Sessions that tapped checkout (includes abandoned)."
            dim={false}
          />
          <FunnelStep
            icon={<FaChartBar className="text-emerald-600" />}
            label="Order placed (completed)"
            value={String(orderVolume)}
            sub={`${selectedRangeLabel} · scoped to your filter`}
            dim={false}
          />
        </div>
      </section>
      )}
    </div>
  );
}

function FunnelStep({
  icon,
  label,
  value,
  sub,
  dim,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  sub: string;
  dim?: boolean;
}) {
  return (
    <div
      className={`flex items-center gap-4 rounded-lg border p-4 ${
        dim ? 'border-gray-200 bg-gray-50' : 'border-emerald-200 bg-emerald-50'
      }`}
    >
      <div className="text-xl flex-shrink-0">{icon}</div>
      <div className="flex-1 min-w-0">
        <div className="font-semibold text-gray-900">{label}</div>
        <div className="text-xs text-gray-600">{sub}</div>
      </div>
      <div className="text-xl font-bold text-gray-900 tabular-nums">{value}</div>
    </div>
  );
}
